import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { BACKUP_COLLECTIONS, fmtDate } from "../lib/labels";
import { FormError } from "./Common";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";

const Delta = ({ now, then }) => {
  const d = then - now;
  const cls = d === 0 ? "text-slate-500" : d > 0 ? "text-emerald-300" : "text-rose-300";
  return <span className={`font-mono ${cls}`}>{d === 0 ? "=" : d > 0 ? `+${d}` : d}</span>;
};

const CompareTable = ({ current, counts }) => (
  <table className="w-full text-left text-sm" data-testid="restore-compare-table">
    <thead className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
      <tr><th className="py-1.5">Dados</th><th className="py-1.5 text-right">Atual</th><th className="py-1.5 text-right">Cópia</th><th className="py-1.5 text-right">Diferença</th></tr>
    </thead>
    <tbody>
      {Object.entries(BACKUP_COLLECTIONS).map(([k, label]) => (
        <tr key={k} className="border-t border-slate-800/70" data-testid={`restore-compare-${k}`}>
          <td className="py-1.5 text-slate-300">{label}</td>
          <td className="py-1.5 text-right font-mono">{current?.[k] ?? 0}</td>
          <td className="py-1.5 text-right font-mono">{counts?.[k] ?? 0}</td>
          <td className="py-1.5 text-right"><Delta now={current?.[k] ?? 0} then={counts?.[k] ?? 0} /></td>
        </tr>
      ))}
    </tbody>
  </table>
);

export const BackupRestoreDialog = ({ backup, current, onClose, onDone }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { setText(""); setError(""); }, [backup]);

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/admin/backups/${backup.id}/restore`, { confirm: text });
      toast.success(`Cópia restaurada.${data.wars_cancelled ? ` ${data.wars_cancelled} guerra(s) ativa(s) cancelada(s).` : ""}`);
      onDone();
    } catch (e) {
      setError(errMsg(e, "Não foi possível restaurar a cópia."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!backup} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-lg border-slate-800 bg-slate-950" data-testid="restore-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-wider">Restaurar cópia de segurança</DialogTitle>
          <DialogDescription className="text-slate-400">Cópia de {fmtDate(backup?.created_at)}</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2 border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-200" data-testid="restore-warning">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-400" />
          <span>Organizações, zonas, receitas, configurações, temporadas, guerras e registo de crafts serão <b>substituídos</b> pelos da cópia. Guerras ativas da cópia ficam canceladas. Contas de admin e auditoria não são alteradas. É criada antes uma cópia pré-restauro.</span>
        </div>
        <CompareTable current={current} counts={backup?.counts} />
        <label className="block space-y-1.5">
          <span className="font-mono text-[11px] uppercase tracking-widest text-slate-400">Escreva RESTAURAR para confirmar</span>
          <Input value={text} onChange={(e) => setText(e.target.value)} autoComplete="off" data-testid="restore-confirm-input" className="border-slate-700 bg-slate-900 font-mono" />
        </label>
        <FormError message={error} testid="restore-error" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} className="border-slate-700 bg-transparent" data-testid="restore-cancel-button">Voltar</Button>
          <Button onClick={submit} disabled={busy || text !== "RESTAURAR"} className="bg-rose-600 text-white hover:bg-rose-500" data-testid="restore-submit-button">
            {busy ? "A restaurar…" : "Restaurar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
